import { RouterLink } from '@redux-first-router-link';

import { Grid, Box, Text, useTheme } from '@simplywallst/ui-core';
import RewardSolid from '@simplywallst/ui-core/icons/RewardSolid';
import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';

import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { ROUTE_PORTFOLIO } from '@/constants/routes';

type Props = {
  portfolioName?: string;
  portfolioId: string;
};

export const EmptyState = ({ portfolioName, portfolioId }: Props) => {
  const theme = useTheme();

  return (
    <RouterLink
      to={{
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId },
      }}
      noUnderline
    >
      <Box
        minWidth="288px"
        backgroundColor="noir"
        borderRadius="x1"
        overflow="hidden"
        data-testid="portfolio-card-empty-state"
      >
        <Grid templateRows="auto 55px 56px" gap="8px">
          <Box paddingXSm="x3" paddingX="x2" paddingY="x2">
            <Grid templateColumns="auto 80px">
              <Grid templateRows="45px auto" gap="8px">
                <Grid>
                  <Text typography="baseStrong" isTruncated>
                    {portfolioName || 'Portfolio'}
                  </Text>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                    0 holdings
                  </Text>
                </Grid>
                <Grid
                  templateColumns="repeat(auto-fit, minmax(max-content, 70px))"
                  templateColumnsSm="repeat(3, max-content)"
                  gap="16px"
                >
                  <Grid>
                    <Text typography="xSmall">-</Text>
                    <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                      Total Value
                    </Text>
                  </Grid>
                  <Grid>
                    <Text typography="xSmall">-</Text>
                    <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                      Total Return
                    </Text>
                  </Grid>
                  <Grid>
                    <Text typography="xSmall">-</Text>
                    <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                      Div. Yield
                    </Text>
                  </Grid>
                </Grid>
              </Grid>
              <Grid justifyContent="center" alignItems="start">
                <Box width="80px" height="80px" position="relative">
                  <Snowflake data-testid="snowflake" points={[1, 1, 1, 1, 1]}>
                    <Snowflake.DotMarkers />
                  </Snowflake>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Grid alignItems="center" templateRows="55px">
            <Box position="relative">
              <Box height="2px" backgroundColor="white" />
              <Box
                position="absolute"
                width="100%"
                height="28px"
                style={{
                  background: `linear-gradient(
                                180deg,
                                ${theme.color.whiteRGBA(0.1)} 0%,
                                ${theme.color.whiteRGBA(0)} 99.36%
                              )`,
                }}
              />
            </Box>
          </Grid>
          <Box paddingXSm="x3" paddingX="x2" paddingTop="x1" paddingBottom="x2">
            <Grid
              templateColumns="45px 45px"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid templateColumns="auto auto" gap="8px">
                <Box borderRadius="x0_5" backgroundColor="spaceGray">
                  <Grid
                    padding="x0_5"
                    paddingRight="x1_5"
                    paddingLeft="x1"
                    borderRadius="x0_5"
                    templateColumns="max-content max-content"
                    alignItems="center"
                    gap="2px"
                    opacity={0.3}
                    data-testid="risk-count-container"
                  >
                    <Box position="relative" width={24} height={24}>
                      <Box
                        position="absolute"
                        backgroundColor="white"
                        borderRadius="x3"
                        width={12}
                        height={12}
                        transform="translate(50%, 50%)"
                      />
                      <Box position="relative">
                        <WarningSolid fill={theme.color.bad} />
                      </Box>
                    </Box>
                    <Text typography="tiny">-</Text>
                  </Grid>
                </Box>
                <Box borderRadius="x0_5" backgroundColor="spaceGray">
                  <Grid
                    padding="x0_5"
                    paddingRight="x1_5"
                    paddingLeft="x1"
                    borderRadius="x0_5"
                    templateColumns="max-content max-content"
                    alignItems="center"
                    opacity={0.3}
                    data-testid="reward-count-container"
                  >
                    <RewardSolid fill={theme.color.good} />
                    <Text typography="tiny">-</Text>
                  </Grid>
                </Box>
              </Grid>
              <Grid>
                <Box
                  borderRadius="x0_5"
                  style={{
                    backgroundColor: theme.color.goodRGBA(0.1),
                  }}
                >
                  <Grid
                    padding="x0_5"
                    paddingX="x1"
                    borderRadius="x0_5"
                    justifyContent="center"
                    alignItems="center"
                    templateColumns="max-content"
                    templateRows="24px"
                  >
                    <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                      1Y -
                    </Text>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </RouterLink>
  );
};
